import { BlockParams } from './blockParams';
import { Bounding } from '../../core/models/bounding';
import { BoxElement } from './elements/boxElement';
import { Element } from '../../equipment/models/elements/element';
import { EquipmentBase } from './equipment-base';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { HoleElement } from './elements/holeElement';
import { HookElement } from './elements/hookElement';
import { IPlanogram } from './planogram';
import { IPlanogramBlockData } from './planogramData';
import { Point } from '../../core/models/point';
import { Product } from './product';
import { ProductGroup } from './product-group';
import { Range } from '../../core/models/range';
import { Rectangle } from '../../equipment/models/figures/rectangle';
import { Side } from '../../core/models/side';

import { Utils } from '../../core/services/utils.service';
import { IProperty, ITreeProperty } from './product-base';

const CACHE_DEFAULTS: BlockCache = {
		coordinates: null,
		elements: null,
		elementsMatrix: null,
		productsMatrix: null,
		percent: null,
		meters: null
	},
	COLORS: Array<string> = [
		'#C0C0C066', '#80808066', '#00000066', '#FF000066', '#80000066', '#FFFF0066', '#80800066', '#00FF0066',
		'#00800066', '#00FFFF66', '#00808066', '#0000FF66', '#00008066', '#FF00FF66', '#80008066', '#99000066',
		'#66000066', '#99260066', '#661a0066', '#994d0066', '#66330066', '#99730066', '#664d0066', '#99990066',
		'#66660066', '#73990066', '#4d660066', '#4d990066', '#33660066', '#26990066', '#1a660066', '#00990066',
		'#00660066', '#00992666', '#00661a66', '#00994d66', '#00663366', '#00997366', '#00664d66', '#00999966',
		'#00666666', '#00739966', '#004d6666', '#004d9966', '#00336666', '#00269966', '#001a6666', '#00009966',
		'#00006666', '#26009966', '#1a006666', '#4d009966', '#33006666', '#73009966', '#4d006666', '#99009966',
		'#66006666', '#99007366', '#66004d66', '#99004d66', '#66003366', '#99002666', '#66001a66'
	],
	PERFORATIONS_LIST: Array<EquipmentElements> = [EquipmentElements.Perforation, EquipmentElements.PerforatedWall];

function checkOpacity(color: string): string {
	if (color.length == 7) return color + '66';
	return color;
}

interface BlockCache {
	coordinates?: Array<Point>,
	elements?,
	elementsMatrix?,
	productsMatrix?,
	percent?: number,
	meters?: number
}

export class Block {
	localId: number;
	id: number;
	name: string = '';
	description: string;
	image: string;
	begin: Array<Point>;
	width: number;
	height: number;
	depth: number;
	color: string = COLORS[0];
	stand: EquipmentBase;
	standId: number;
	standHeight: number;
	standBegin: number;
	products: Array<ProductGroup> = [];
	error: boolean = false;
	baseHeight: number;
	standTotalHeight: number;
	cache: BlockCache = Object.assign({}, CACHE_DEFAULTS);
	private _assortmentTypes: Array<number> = [];
	private _productStatuses: Array<number> = [];
	private _brands: Array<number> = [];
	private _categories: Array<number> = [];
	private _markers: Array<number> = [];
	private _suppliers: Array<number> = [];

	get boundingX(): Bounding {
		const xList: Array<number> = this.coordinates.map((point: Point) => point.x);

		return {
			min: Utils.round(Utils.min(xList)),
			max: Utils.round(Utils.max(xList))
		};
	}

	get boundingY(): Bounding {
		const yList: Array<number> = this.coordinates.map((point: Point) => point.y);

		return {
			min: Utils.round(this.stand.Height - Utils.max(yList)),
			max: Utils.round(this.stand.Height - Utils.min(yList))
		};
	}

	get boundingZ(): Bounding {
		const zList: Array<number> = this.coordinates.map((point: Point) => point.z);

		return {
			min: Utils.round(Utils.min(zList)),
			max: Utils.round(Utils.max(zList))
		};
	}

	set assortmentTypes(assortmentTypes: Array<number>) {
		this._assortmentTypes = assortmentTypes;
	}

	get assortmentTypes(): Array<number> {
		return this._assortmentTypes.filter((type: number) => type !== -1);
	}

	set productStatuses(productStatuses: Array<number>) {
		this._productStatuses = productStatuses;
	}

	get productStatuses(): Array<number> {
		return this._productStatuses.filter((status: number) => status !== -1);
	}

	set brands(brands: Array<number>) {
		this._brands = brands;
	}

	get brands(): Array<number> {
		return this._brands.filter((brand: number) => brand !== -1);
	}

	set categories(value: Array<number>) {
		this._categories = value;
	}

	get categories(): Array<number> {
		return this._categories;
	}

	get colorAlpha(): number {
		return parseInt(this.color.slice(7, 9) || 'ff', 16) / 255;
	}

	get coordinates(): Array<Point> {
		if (!this.cache.coordinates) {
			this.cache.coordinates = [
				{ x: this.begin[0].x, y: this.begin[0].y, z: this.begin[0].z },
				{ x: Utils.round(this.begin[0].x + this.width), y: this.begin[0].y, z: this.begin[0].z },
				{
					x: Utils.round(this.begin[0].x + this.width),
					y: Utils.round(this.begin[0].y + this.height),
					z: Utils.round(this.begin[0].z + this.depth)
				},
				{ x: this.begin[0].x, y: Utils.round(this.begin[0].y + this.height), z: this.begin[0].z },
				{ x: this.begin[0].x, y: this.begin[0].y, z: Utils.round(this.begin[0].z + this.depth) }
			];
		}

		return this.cache.coordinates;
	}

	get elements() {
		if (!this.stand) return [];

		if (!this.cache.elements) {
			const blockBoundingX: Bounding = this.boundingX,
				blockBoundingY: Bounding = this.boundingY,
				elementsPolygons = this.stand.availableElements.filter(el =>
					!['BackWall', 'Showcase', 'HookWall', 'HookPanel', 'Partition', 'SidesWall', 'Perforation', 'PerforationWall', 'CentralPillar'].includes(el.parent.type)
				);

			this.cache.elements = elementsPolygons.filter(el => {
				const elementBoundingX: Bounding = el.getBoundingX(),
					elementBoundingY: Bounding = el.getBoundingY();

				if (elementBoundingX.min > blockBoundingX.max || elementBoundingX.max < blockBoundingX.min) return false;

				switch (el.parent.type) {
					case 'AngleShelf': {
						const comparisonPoint: number = Utils.round(
							elementBoundingY.min + (el.shapes[0] as Rectangle).height
						);
						return blockBoundingY.min <= comparisonPoint && blockBoundingY.max > comparisonPoint;
					}
					case 'Box':
					case 'SlicedBox':
						return blockBoundingY.min <= elementBoundingY.min && blockBoundingY.max > elementBoundingY.min;
					case 'Hook':
					case 'Hole': {
						const comparisonPoint: number = Utils.round(
							elementBoundingX.min + (elementBoundingX.max - elementBoundingX.min) / 2
						);
						if (comparisonPoint > blockBoundingX.max || comparisonPoint <= blockBoundingX.min) return false;
						return blockBoundingY.min <= elementBoundingY.min && blockBoundingY.max > elementBoundingY.min;
					}
					default:
						return blockBoundingY.min <= elementBoundingY.max && blockBoundingY.max > elementBoundingY.max;
				}
			});
		}

		return this.cache.elements;
	}

	get elementsMatrix() {
		if (!this.cache.elementsMatrix) {
			const list = this.elements;

			let tempList = [],
				resultList = [];

			list.sort((a, b) => a.constructor.name - b.constructor.name);

			for (let i = 0; i < list.length; i++) {
				tempList.push(list[i]);
				if (i != list.length - 1 && list[i].constructor.name == list[i + 1].constructor.name) continue;
				tempList.sort((a, b) => a.shapes[0].begin.y - b.shapes[0].begin.y || a.shapes[0].begin.x - b.shapes[0].begin.x);
				resultList.push(tempList);
				tempList = [];
			}

			this.cache.elementsMatrix = resultList;
		}

		return this.cache.elementsMatrix;
	}

	get filterAssortmentTypes(): Array<number> {
		return this._assortmentTypes;
	}

	get filterProductStatuses(): Array<number> {
		return this._productStatuses;
	}

	get filterBrands(): Array<number> {
		return this._brands;
	}

	get filterSuppliers(): Array<number> {
		return this._suppliers;
	}

	get hasEmptySettings(): boolean {
		return !this.categories.length &&
			!this.brands.length &&
			!this.suppliers.length &&
			!this.markers.length &&
			!this.assortmentTypes.length &&
			!this.productStatuses.length;
	}

	set markers(value: Array<number>) {
		this._markers = value;
	}

	get markers(): Array<number> {
		return this._markers;
	}

	get meters(): number {
		return this.cache.meters || 0;
	}

	get percent(): number {
		if (!this.cache.percent) {
			const blockBounding: Bounding = this.boundingX;
			const shelfMeters: number = this.stand.getShelfMeters(false);
			const hangBarMeters: number = this.stand.getHangBarMeters();
			const hookMeters: number = this.stand.getHooksMeters();
			const holeMeters: number = this.stand.getHolesMeters();
			const planogramMeters: number = Utils.round(shelfMeters + hangBarMeters + hookMeters + holeMeters);
			const blockShelvesMeters: number = this.elements
				.filter((el: Element) => !el.is(HookElement, HoleElement, BoxElement))
				.reduce((total: number, element: Element) => {
					const bounding: Bounding = { min: 0, max: 0 };
					const elementBounding: Bounding = element.getBoundingX();

					bounding.min = elementBounding.min < blockBounding.min ? blockBounding.min : elementBounding.min;
					bounding.max = elementBounding.max > blockBounding.max ? blockBounding.max : elementBounding.max;

					const baseWithCentralPillar: boolean = this.stand.allElements
						.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar) && el.depend == element);
					const twoSided: boolean = element.depend?.parent.is(EquipmentElements.CentralPillar) || baseWithCentralPillar;

					if (twoSided) {
						return Utils.round(total + (bounding.max - bounding.min) * 2);
					}

					return Utils.round(total + (bounding.max - bounding.min));
				}, 0);
			const blockRowsMeters: number = this.getRowsMeters(
				this.elements.filter((el: Element) => el.is(HookElement, HoleElement))
			);
			const blockMeters: number = Utils.round(blockShelvesMeters + blockRowsMeters);

			this.cache.meters = blockMeters;
			this.cache.percent = planogramMeters ? Utils.round(blockMeters / planogramMeters * 100, 2) : 0;
		}

		return this.cache.percent;
	}

	get productsMatrix() {
		return this.products.map(pg => Utils.to1DArray(this.elementsMatrix).findIndex(el => pg.element == el));
	}

	set suppliers(value: Array<number>) {
		this._suppliers = value;
	}

	get suppliers(): Array<number> {
		return this._suppliers.filter((supplier: number) => supplier !== -1);
	}

	static fromJSON(data: IPlanogramBlockData, stand: EquipmentBase, products: Array<ProductGroup>): BlockParams {
		return {
			id: data.id,
			name: data.name,
			description: data.description,
			image: data.image,
			begin: data.conf.begin,
			width: data.conf.width,
			height: data.conf.height,
			depth: data.conf.depth,
			color: data.conf.color,
			assortmentTypes: data.assortment_types,
			productStatuses: data.product_statuses,
			categories: data.categories,
			brands: data.brands,
			suppliers: data.suppliers,
			markers: data.markers,
			stand: stand,
			standId: data.stand,
			standHeight: data.conf.standHeight,
			standBegin: data.conf.standBegin,
			products: [...products],
			error: data.conf.error
		};
	}

	static setBlockTitle(blocksNames: Array<string>, name: string = 'Block 1'): string {
		if (blocksNames.some((blockName: string) => blockName.toLowerCase().includes(name.toLowerCase()))) {
			name = name.replace(/\d+/ig, (foundNum: string) => String(Number(foundNum) + 1));
			name = Block.setBlockTitle(blocksNames, name);
		}

		return name;
	}

	constructor(params: BlockParams) {
		this.localId = Math.floor(Math.random() * Math.pow(10, 12));

		this.begin = [{
            x: Utils.round(params.begin[0].x),
            y: Utils.round(params.begin[0].y),
            z: Utils.round(params.begin[0].z || 0)
        }];

		if (params.id) this.id = params.id;
		if (params.name) this.name = params.name;
		if (params.description) this.description = params.description;
		if (params.image) this.image = params.image;
		if (params.width) this.width = Utils.round(params.width);
		if (params.height) this.height = Utils.round(params.height);
		if (params.depth) this.depth = Utils.round(params.depth);
		if (params.color) this.color = checkOpacity(params.color);
		if (params.assortmentTypes?.length) this.assortmentTypes = [...params.assortmentTypes];
		if (params.productStatuses?.length) this.productStatuses = [...params.productStatuses];
		if (params.categories?.length) this.categories = [...params.categories];
		if (params.brands?.length) this.brands = [...params.brands];
		if (params.suppliers?.length) this.suppliers = [...params.suppliers];
		if (params.markers?.length) this.markers = [...params.markers];
        if (params.standId) this.standId = params.standId;
        if (params.standHeight) this.standHeight = params.standHeight;
        if (params.standBegin) this.standBegin = params.standBegin;
		if (params.products) this.products = params.products;
		if (params.error) this.error = params.error;

		if (params.stand) {
			this.stand = params.stand;

			const backWall: Element = this.stand.allElements
					.find(s =>
						['BackWall', 'HookWall', 'Showcase', 'PerforationWall'].includes(s.parent.type) &&
						s.shapes[0].isPointOn(this.begin[0].x)
					),
				base: Element = this.stand.bases.find(s => s.shapes[0].isPointOn(this.begin[0].x)),
				backWallHeight = backWall?.Height || 0;

			this.baseHeight = base?.Height || 0;
			this.standTotalHeight = backWallHeight ? backWallHeight + this.baseHeight : this.stand.Height;
		}
	}

	clearCache(): void {
		this.cache = Object.assign({}, CACHE_DEFAULTS);
	}

	copy(): Block {
		const block: Block = new Block(Block.fromJSON(this.toJSON(true), this.stand, this.products));
		block.localId = this.localId;
		return block;
	}

	extractPgElement(elementIndex: number) {
		return Utils.to1DArray(this.elementsMatrix)[elementIndex];
	}

	getGroupByOption(hierarchy) {
		for (let by_option in hierarchy) {
			if (this[hierarchy[by_option]]) return hierarchy[by_option];
		}

		return null;
	}

	getRowsMeters(elements: Array<Element>): number {
		const blockBoundingX: Bounding = this.boundingX;
		const parentElements: Array<Element> = elements
			.map((element: Element) => element.depend)
			.filter((element: Element) => !element.parent.is(EquipmentElements.HangBar));

		return Utils.unique(parentElements)
			.map((panel: Element) => {
				return panel.stand.getElementsRows(panel)
					.filter((row: Array<Element>) => {
						const isPerforation: boolean = PERFORATIONS_LIST.includes(panel.parent.type);
						return !isPerforation || row.some((element: Element) => element.products.length);
					})
					.map((row: Array<Element>) => {
						return {
							width: panel.Width,
							boundingX: panel.getBoundingX(),
							boundingY: {
								min: row[row.length - 1].shapes[0].begin.y + row[row.length - 1].shapes[0].height,
								max: row[0].shapes[0].begin.y + row[0].shapes[0].height
							}
						};
					})
					.filter((row) => {
						const bounding: Bounding = this.boundingY,
							max: boolean = row.boundingY.max <= bounding.max && row.boundingY.max > bounding.min,
							min: boolean = row.boundingY.min >= bounding.min && row.boundingY.min < bounding.max;
						return max && min;
					})
					.reduce((total: number, row) => {
						const bounding: Bounding = { min: 0, max: 0 };

						bounding.min = row.boundingX.min < blockBoundingX.min ? blockBoundingX.min : row.boundingX.min;
						bounding.max = row.boundingX.max > blockBoundingX.max ? blockBoundingX.max : row.boundingX.max;

						return Utils.round(total + (bounding.max - bounding.min));
					}, 0);
			})
			.reduce((total: number, width: number) => Utils.round(total + width), 0);
	}

	hasIntersectionsWithProducts(): boolean {
		const blockBoundingX: Bounding = this.boundingX,
			blockBoundingY: Bounding = this.boundingY,
			products: Array<ProductGroup> = this.stand.products.filter((pg: ProductGroup) => {
				if (this.products.includes(pg)) return true;

				const el: Element = pg.element,
					boundingX: Bounding = pg.getBoundingX(),
					boundingY: Bounding = pg.getBoundingY();

				if (el.parent.type == 'AngleShelf' || (el.depend && el.depend.parent.type == 'AngleShelf')) {
					boundingY.min = Utils.round(boundingY.min - el.Height);
					boundingY.max = Utils.round(boundingY.max - el.Height);
				}

				return !(boundingX.min >= blockBoundingX.max || boundingX.max <= blockBoundingX.min) &&
					!(boundingY.min >= blockBoundingY.max || boundingY.max <= blockBoundingY.min);
			});

		this.error = products.some((pg: ProductGroup) => {
			const el: Element = pg.element,
				boundingX: Bounding = pg.getBoundingX(),
				boundingY: Bounding = pg.getBoundingY(),
				boundingZ: Bounding = pg.getBoundingZ();

			if (el.parent.type == 'AngleShelf' || (el.depend && el.depend.parent.type == 'AngleShelf')) {
				boundingY.min = Utils.round(boundingY.min - el.Height);
				boundingY.max = Utils.round(boundingY.max - el.Height);
			}

			return boundingX.min < blockBoundingX.min ||
				boundingX.max > blockBoundingX.max ||
				boundingY.min < blockBoundingY.min ||
				boundingY.max > blockBoundingY.max ||
				boundingZ.min < this.begin[0].z;
		});

		return this.error;
	}

	inRange(range: Range): boolean {
		const xBounding: Bounding = this.boundingX;

		return !(range.to <= xBounding.min || range.from >= xBounding.max);
	}

	isPointOn(x?: number, y?: number): boolean {
		if (x != null) {
			const xBounding: Bounding = this.boundingX;
			if (x < xBounding.min || x > xBounding.max) return false;
		}

		if (y != null) {
			const yBounding: Bounding = this.boundingY;
			if (y < yBounding.min || y > yBounding.max) return false;
		}

		return true;
	}

	isProductSuitable(product: Product): boolean {
		const errors: Array<boolean> = [
			this.categories.length && this.categories.every((category: number) =>
				!Utils.to1DArray(product.categories).map((c: ITreeProperty) => c.id).includes(category)
			),
			this.markers.length && this.markers.every((marker: number) =>
				!Utils.to1DArray(product.markers).map((m: ITreeProperty) => m.id).includes(marker)
			),
			this.suppliers.length && this.suppliers.every((supplier: number) =>
				!product.suppliers.map((s: IProperty) => s.id).includes(supplier)
			),
			this.brands.length && this.brands.every((brand: number) =>
				!product.brands.map((b: IProperty) => b.id).includes(brand)
			),
			this.assortmentTypes.length && !this.assortmentTypes.includes(product.assortment_type.id),
			this.productStatuses.length && this.productStatuses.every((status: number) =>
				!product.statuses.map((s: IProperty) => s.id).includes(status)
			)
		];

		return !errors.some((condition: boolean) => condition);
	}

	resize(blockX: number, blockY: number, side: Side): void {
		switch (side) {
			case 'left':
				this.width = Utils.round(this.width + this.begin[0].x - blockX);
				this.begin[0].x = Utils.round(blockX);
				break;

			case 'right':
				this.width = Utils.round(blockX - this.begin[0].x);
				break;

			case 'top':
				this.height = Utils.round(this.height + this.begin[0].y - blockY);
				this.begin[0].y = Utils.round(blockY);
				break;

			case 'bottom':
				this.height = Utils.round(blockY - this.begin[0].y);
				break;
		}

		this.clearCache();
	}

	setBlockColor(): void {
		this.color = COLORS[Math.floor(Math.random() * (COLORS.length - 1))];
	}

	setProducts(otherBlockProducts: Array<ProductGroup> = []): Array<ProductGroup> {
		const blockBoundingX: Bounding = this.boundingX,
			products: Array<ProductGroup> = this.stand.products.filter(
				(p: ProductGroup) => (!p.block && !otherBlockProducts.includes(p)) || (p.block && p.block == this.id)
			);

		let blockProducts: Array<ProductGroup> = products.filter((pg: ProductGroup) => {
				const boundingX: Bounding = pg.getBoundingX();

				return !(boundingX.min >= blockBoundingX.max || boundingX.max <= blockBoundingX.min) &&
					this.elements.some(el => [pg.element, ...pg.element.getSiblingElements].includes(el));
			}),
			outOfBlock: Array<ProductGroup>;

		outOfBlock = blockProducts.filter(
			(product: ProductGroup) => !this.isProductSuitable(product.product)
		);

		blockProducts = blockProducts.filter(
			(product: ProductGroup) => this.isProductSuitable(product.product)
		);

		this.products = blockProducts;
		this.products.forEach((p: ProductGroup) => p.block = this.id);

		return outOfBlock;
	}

	setStand(stand: IPlanogram): void {
		this.stand = stand.stand;
		this.standId = stand.id;
	}

	toJSON(isCopy: boolean = false): IPlanogramBlockData {
		return {
			id: this.id,
			name: this.name,
			description: this.description,
			image: this.image,
			stand: this.standId,
			assortment_types: isCopy ? this.filterAssortmentTypes : this.assortmentTypes,
			product_statuses: isCopy ? this.filterProductStatuses : this.productStatuses,
			categories: this.categories,
			brands: isCopy ? this.filterBrands : this.brands,
			suppliers: isCopy ? this.filterSuppliers : this.suppliers,
			markers: this.markers,
			percent: this.percent,
			linear_meters: this.meters,
			conf: {
				begin: [Object.assign({}, this.begin[0])],
				width: this.width,
				height: this.height,
				depth: this.depth,
				products: this.products?.map((p: ProductGroup) => {
					return {
						id: p.product.id,
						begin: {
							x: Utils.round(p.begin.x),
							y: Utils.round(p.begin.y),
							z: Utils.round(p.begin.z)
						}
					};
				}),
				percent: this.percent,
				color: this.color,
				standHeight: this.standHeight,
				standBegin: this.standBegin,
				error: this.error
			}
		};
	}

	updateBlockProducts(products: Array<ProductGroup>): void {
		this.products = products;
		this.clearCache();
	}

	updateConfig(params: any): void {
		this.name = params.name;
		this.description = params.description;
		this.image = params.image;

		if (params.height) this.height = Utils.round(params.height);
		if (params.width) this.width = Utils.round(params.width);
		if (params.depth) this.depth = Utils.round(params.depth);

		this.assortmentTypes = [...(params.assortmentTypes || [])];
		this.productStatuses = [...(params.productStatuses || [])];
		this.categories = [...(params.categories || [])];
		this.brands = [...(params.brands || [])];
		this.suppliers = [...(params.suppliers || [])];
		this.markers = [...(params.markers || [])];

		this.clearCache();
	}

	updateCoordinates(begin: Array<Point>, width: number = this.width, height: number = this.height): void {
		this.begin = [{
			x: Utils.round(begin[0].x),
			y: Utils.round(begin[0].y),
			z: Utils.round(begin[0].z || 0)
		}];
		this.width = Utils.round(width);
		this.height = Utils.round(height);

		this.clearCache();
	}

	updateCoordinatesForReverseDrawBlock(x, y) {
		this.cache.coordinates = [
			{ x: this.begin[0].x, y: this.begin[0].y, z: this.begin[0].z },
			{
				x: Utils.round(this.begin[0].x + (x ? -this.width : this.width)),
				y: this.begin[0].y,
				z: this.begin[0].z
			},
			{
				x: Utils.round(this.begin[0].x + (x ? -this.width : this.width)),
				y: Utils.round(this.begin[0].y + (x && !y ? this.height : -this.height)),
				z: Utils.round(this.begin[0].z + this.depth)
			},
			{
				x: this.begin[0].x,
				y: Utils.round(this.begin[0].y + (x && !y ? this.height : -this.height)),
				z: this.begin[0].z
			},
			{ x: this.begin[0].x, y: this.begin[0].y, z: Utils.round(this.begin[0].z + this.depth) }
		];
	}
}
